import React, { useState, type FC, type PropsWithChildren, type TouchEventHandler, useEffect, useContext } from 'react';

import { isAnchorPath } from '@lucid/utils';
import { getColor } from '@lucid/theme';
import { withImage, useSmartlingStrings } from '@lucid/hooks';
import kebabCase from 'lodash/kebabCase';

import ImageWithPlaceholder from '../image/image-with-placeholder';
import type { Lucid } from '../../types/types';
import Box from '../box/box';
import ControlButton, { type ControlButtonProps } from '../buttons/control-button';
import Text from '../text/text';
import Link from '../link/link';
import Heading from '../heading/heading';
import LayoutContext from '../context/layout-context';
import VisibleLottie from '../lottie/visible-lottie';
import VisibleVideo from '../video/visible-video/visible-video';
import Icon from '../icon/icon';
import Container from '../container/container';
import Button from '../button/button';

export interface MediaCarouselProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The heading for the `Carousel` section. */
  items?: Lucid.Directus.Generated.Carousel_Item | Lucid.Directus.Generated.Carousel_Item[];
  /** Translated aria label for carousel section */
  ariaLabel?: string;
  /** Sets the time to change the CarouselSlider image. @default 7000 = 4s */
  sliderTime?: number;
  /** The id to use for aria purposes */
  id?: string;
  /** Boolean for if header should be below the imagery or above.  */
  hasLowerHeading?: boolean;
  /** Used for hero-carousel, thiswill stop the carousel if the specified element is not in view */
  isInView?: boolean;
  /** Specify the hero's background color */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Determines whether or not the carousel text context should be displayed */
  shouldShowContent?: boolean;
}

export type CarouselControlButtonProps = Pick<ControlButtonProps, 'aria-controls' | 'aria-label' | 'onClick'> & {
  /**  Direction of the button, either 'previous' or 'next' */
  direction: 'previous' | 'next';
  /** Flag to determine if the button is for mobile view */
  isMobile?: boolean;
};

const CarouselControlButton: React.FC<CarouselControlButtonProps> = ({ direction, onClick, isMobile, ...rest }) => {
  const isPrevious = direction === 'previous';

  return (
    <ControlButton
      {...rest}
      css={theme => ({
        transform: isPrevious ? 'rotate(180deg)' : 'none',
        minWidth: theme.space.s48,
        width: theme.space.s48,
        height: theme.space.s48,
        display: isMobile ? 'flex' : 'none',
        [theme.breakpoints.bp900.min]: {
          display: isMobile ? 'none' : 'flex',
        },
      })}
      iconType="ArrowRight"
      onClick={onClick}
    />
  );
};

const MediaCarousel: FC<PropsWithChildren<MediaCarouselProps>> = ({
  className,
  ariaLabel = '',
  hasLowerHeading,
  items,
  id = 'media-carousel',
  sliderTime = 7000,
  backgroundColor,
  isInView = true,
  shouldShowContent = false,
  ...rest
}) => {
  const carouselItems = items || [];
  const isItemArray = Array.isArray(carouselItems);
  const numItems = isItemArray ? carouselItems.length : 1;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSlideshowPaused, setIsSlideshowPaused] = useState(false);
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);

  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_carousel_slide_previous',
      'mktg_carousel_slide_next',
      'mktg_ct_add_on_pause_animation_label',
      'mktg_ct_add_on_play_animation_label',
    ],
    shortCode,
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    const nextIndex = (selectedIndex + 1) % numItems;

    if (isInView && !isSlideshowPaused) {
      timeoutId = setTimeout(() => {
        setSelectedIndex(nextIndex);
      }, sliderTime);
    }

    if (timeoutId) {
      return () => clearTimeout(timeoutId as NodeJS.Timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numItems, selectedIndex, isInView, isSlideshowPaused]);

  // Slideshow/Animation event handlers
  const handleUpdateCarousel = (index: number) => {
    setSelectedIndex(index < 0 ? numItems - 1 : index % numItems);
    setIsSlideshowPaused(true);
    setIsAnimationPaused(true);
  };

  const handleTogglePause = () => {
    if (isSlideshowPaused && isAnimationPaused) {
      setIsAnimationPaused(false);
      setIsSlideshowPaused(false);
    } else {
      setIsSlideshowPaused(true);
      setIsAnimationPaused(true);
    }
  };

  // Swipe controls
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchEnd: TouchEventHandler = () => {
    if (touchStart - touchEnd > 150) {
      handleUpdateCarousel(selectedIndex + 1);
    }

    if (touchStart - touchEnd < -150) {
      handleUpdateCarousel(selectedIndex - 1);
    }
  };

  // Wrapping heading, description, etc. in this wrapper gives all text containers a uniform height,
  // and prevents items from shifting when clicking through carousel, regardless of differing text length
  const carouselWrapperShared = {
    display: 'grid',
    gridTemplateColumns: `repeat(${numItems}, 100%)`,
    transform: `translateX(-${selectedIndex * 100}%)`,
  };

  return (
    <Box
      {...rest}
      as="section"
      css={theme => ({
        label: 'mediaCarousel',
        background: getColor({
          color: backgroundColor,
          theme,
        }),
        display: 'flex',
        alignItems: 'center',
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container
        css={[
          {
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
          },

          isItemArray && {
            '&:hover > button': {
              clip: 'auto',
              clipPath: 'none',
            },
          },
        ]}
      >
        <Button
          aria-label={
            withSmartling(
              `mktg_ct_add_on_${isAnimationPaused || isSlideshowPaused ? 'play' : 'pause'}_animation_label'`,
            ) || 'Click or press Enter to pause animation'
          }
          css={theme => [
            {
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              padding: `${theme.space.s16}px ${theme.space.s24}px`,
              fontSize: theme.fontSizes.fs16,
              position: 'absolute',
              top: '20px',
              left: '20px',
              transform: 'translateX(-50%)',
              cursor: 'pointer',
              zIndex: theme.zIndices.z1000,
              // handles keyboard focus to show the button
              '&:focus-visible': {
                clip: 'auto',
                clipPath: 'none',
              },
            },
            !isItemArray && {
              display: 'none',
            },
          ]}
          onClick={handleTogglePause}
          type="button"
        >
          <Icon type={isAnimationPaused || isSlideshowPaused ? 'Play' : 'Pause'} />
        </Button>
        {!(numItems <= 1) && (
          <CarouselControlButton
            aria-controls={id}
            aria-label={
              withSmartling('mktg_carousel_slide_previous', {
                slide: `${selectedIndex === 0 ? numItems : selectedIndex}`,
              }) || 'Go to previous slide'
            }
            direction="previous"
            onClick={() => handleUpdateCarousel(selectedIndex - 1)}
          />
        )}
        {carouselItems && (
          <>
            {!isItemArray ? (
              // SINGLE IMAGE
              <Box
                as="section"
                css={theme => [
                  {
                    margin: '0 auto',
                    maxWidth: '1100px',
                    width: '100%',
                    paddingBottom: theme.space.s40,
                    paddingLeft: theme.space.s16,
                    paddingRight: theme.space.s16,
                    [theme.breakpoints.bp768.min]: {
                      paddingLeft: theme.space.s40,
                      paddingRight: theme.space.s40,
                    },
                  },
                ]}
              >
                <Box css={{ overflow: 'hidden', width: '100%' }}>
                  <Box
                    css={theme => ({
                      padding: `${theme.space.s40}px 0 ${theme.space.s24}px`,
                    })}
                  >
                    <Heading
                      key={`carousel-header-${carouselItems.heading}`}
                      as="h2"
                      css={{
                        margin: '0 auto',
                        maxWidth: '680px',
                        textAlign: 'center',
                      }}
                      variant="headingXL"
                    >
                      {carouselItems.heading || ''}
                    </Heading>
                  </Box>
                </Box>
                <ImageWithPlaceholder
                  alt={carouselItems.media?.translations?.[0]?.alt || ''}
                  aspectRatio="49:19"
                  css={{
                    img: {
                      margin: 0,
                      height: 'unset',
                    },
                  }}
                  dataElementPageLocation="hero"
                  src={withImage(carouselItems?.media)}
                />
              </Box>
            ) : (
              <Box
                aria-label={ariaLabel.replace('{title}', `${carouselItems?.[selectedIndex]?.heading}`)}
                aria-roledescription="carousel"
                as="section"
                css={theme => [
                  {
                    paddingRight: 0,
                    paddingLeft: 0,
                    margin: '0 auto',
                    maxWidth: '1100px',
                    width: '100%',
                    [theme.breakpoints.bp500.min]: {
                      paddingLeft: theme.space.s16,
                      paddingRight: theme.space.s16,
                    },
                    [theme.breakpoints.bp768.min]: {
                      paddingLeft: theme.space.s40,
                      paddingRight: theme.space.s40,
                    },
                  },
                  hasLowerHeading && {
                    marginTop: theme.space.s24,
                  },
                ]}
              >
                {/* UPPER HEADING */}
                {!hasLowerHeading && (
                  <Box css={{ overflow: 'hidden', width: '100%' }}>
                    <Box
                      css={theme => ({
                        ...carouselWrapperShared,
                        padding: `${theme.space.s40}px 0 ${theme.space.s24}px`,
                      })}
                    >
                      {carouselItems.map((item, idx) => {
                        const isCurrent = idx === selectedIndex;

                        return (
                          <Heading
                            key={item.id || kebabCase(item?.heading)}
                            aria-hidden={!isCurrent}
                            as="h2"
                            css={{
                              margin: '0 auto',
                              maxWidth: '680px',
                              textAlign: 'center',
                              gridColumn: `${idx + 1} / ${idx + 2}`,
                            }}
                            id={`carousel-heading-${idx}`}
                            variant="headingXL"
                          >
                            {item.heading || ''}
                          </Heading>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {/* MEDIA SLIDESHOW */}
                <Box
                  css={theme => ({
                    maxHeight: '380px',
                    margin: '0 auto',
                    overflow: 'hidden',
                    minHeight: '150px',
                    display: 'flex',
                    alignItems: 'center',
                    // Prevent layout shift on image/animation load
                    [theme.breakpoints.bp500.min]: {
                      minHeight: '250px',
                    },
                    [theme.breakpoints.bp768.min]: {
                      minHeight: '300px',
                    },
                    [theme.breakpoints.bp900.min]: {
                      minHeight: '380px',
                    },
                  })}
                >
                  <Box
                    aria-live="off"
                    css={theme => ({
                      ...carouselWrapperShared,
                      width: '100%',
                      maxHeight: 'inherit',
                      transition: `transform 500ms ${theme.beziers.slowStartEase}`,
                    })}
                    id={id}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={e => setTouchEnd(e.targetTouches[0].clientX)}
                    onTouchStart={e => setTouchStart(e.targetTouches[0].clientX)}
                  >
                    {carouselItems.map((item, idx) => {
                      if (!item || !item.media) return null;
                      const { heading, media } = item;
                      const isCurrentSlide = selectedIndex === idx;
                      const mediaType = media?.type?.split('/')[0];

                      // application = json file = lottie animation
                      return mediaType === 'application' || mediaType === 'video' ? (
                        <Box
                          key={item.id || kebabCase(heading)}
                          aria-hidden={!isCurrentSlide}
                          aria-roledescription="slide"
                          css={theme => ({
                            backgroundColor: mediaType === 'application' ? theme.colors.coolGrey[0] : 'none',
                            minWidth: '100%',
                          })}
                        >
                          {mediaType === 'application' ? (
                            // ANIMATION
                            <VisibleLottie
                              hasExternalPauseButton
                              alt={media?.translations?.[0]?.alt || ''}
                              css={{ '#visibleLottie': { maxHeight: '380px' } }}
                              dataElementPageLocation="hero"
                              id={`lottie-animation-${idx + 1}`}
                              path={withImage(media)}
                              willManualOverride={!isCurrentSlide}
                            />
                          ) : (
                            // VIDEO
                            <VisibleVideo
                              hasExternalPauseButton
                              css={{
                                maxWidth: '980px',
                                maxHeight: '380px',
                                boxShadow: 'none',
                                margin: '0 auto',
                              }}
                              description={media?.translations?.[0]?.alt || ''}
                              src={withImage(media)}
                              willManualOverride={!isCurrentSlide}
                            />
                          )}
                        </Box>
                      ) : (
                        // IMAGE
                        <ImageWithPlaceholder
                          key={item.id || kebabCase(heading)}
                          alt={media?.translations?.[0]?.alt || ''}
                          aria-hidden={!isCurrentSlide}
                          aria-roledescription="slide"
                          aspectRatio="49:19"
                          css={{
                            img: {
                              margin: 0,
                              height: 'unset',
                            },
                          }}
                          dataElementPageLocation="hero"
                          src={withImage(media) || ''}
                        />
                      );
                    })}
                  </Box>
                </Box>
                {/* CONTENT */}
                <Box
                  css={theme => ({
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    backgroundColor: theme.colors.coolGrey[5],
                    gap: theme.space.s24,
                    [theme.breakpoints.bp900.min]: { gridTemplateColumns: 'auto 1fr' },
                  })}
                >
                  {/* CONTENT */}
                  {!shouldShowContent && (
                    <Box
                      css={theme => ({
                        overflow: 'hidden',
                        maxWidth: '740px',
                        margin: theme.space.s24,
                        [theme.breakpoints.bp768.min]: {
                          margin: theme.space.s40,
                        },
                      })}
                    >
                      <Box css={carouselWrapperShared}>
                        {carouselItems.map((item, idx) => {
                          const isCurrent = idx === selectedIndex;

                          return (
                            <Box
                              key={`${item.id || kebabCase(item?.text)}-media-carousel-container`}
                              aria-hidden={!isCurrent}
                            >
                              {hasLowerHeading && (
                                <Heading
                                  key={`${item.id || kebabCase(item?.text)}-media-carousel-heading`}
                                  aria-hidden={!isCurrent}
                                  as="h2"
                                  css={theme => ({
                                    margin: '0 auto',
                                    gridColumn: `${idx + 1} / ${idx + 2}`,
                                    textAlign: 'center',
                                    paddingBottom: theme.space.s24,
                                    [theme.breakpoints.bp900.min]: {
                                      margin: 0,
                                      textAlign: 'left',
                                    },
                                  })}
                                  id={`carousel-heading-${idx}`}
                                  variant="headingL"
                                >
                                  {carouselItems?.[selectedIndex]?.heading || ''}
                                </Heading>
                              )}
                              <Text
                                css={theme => ({
                                  margin: '0 auto',
                                  gridColumn: `${idx + 1} / ${idx + 2}`,
                                  textAlign: 'center',
                                  [theme.breakpoints.bp900.min]: {
                                    margin: 0,
                                    textAlign: 'left',
                                  },
                                })}
                                variant="paragraphM400"
                              >
                                {item?.text || ''}
                              </Text>
                              {item?.link?.final_url && item?.link?.default_link_text && (
                                <Box
                                  css={theme => ({
                                    textAlign: 'center',
                                    marginTop: theme.space.s16,
                                    [theme.breakpoints.bp900.min]: { textAlign: 'left', gridRow: '2 / span 1' },
                                  })}
                                >
                                  <Link
                                    aria-describedby={`carousel-heading-${idx}`}
                                    href={item.link.final_url}
                                    iconAnimation={isAnchorPath(item.link.final_url) ? 'jumpLink' : 'navigational'}
                                    onBlur={() => handleTogglePause()}
                                    onFocus={() => handleTogglePause()}
                                    scrollToOptions={{
                                      behavior: 'smooth',
                                      block: 'center',
                                      inline: 'center',
                                    }}
                                    tabIndex={isCurrent ? 0 : -1}
                                  >
                                    {item.link.default_link_text}
                                  </Link>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
                {/* PROGRESS BAR */}
                <Box
                  css={() => [
                    { display: 'flex', justifyContent: 'center', alignItems: 'center' },
                    numItems === 1 && {
                      display: 'none',
                    },
                  ]}
                >
                  <CarouselControlButton
                    isMobile
                    aria-controls={id}
                    aria-label={
                      withSmartling('mktg_carousel_slide_previous', {
                        slide: `${selectedIndex === 0 ? numItems : selectedIndex}`,
                      }) || 'Go to previous slide'
                    }
                    direction="previous"
                    onClick={() => handleUpdateCarousel(selectedIndex - 1)}
                  />
                  <Box
                    css={theme => ({
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: theme.space.s16,
                      margin: theme.space.s48,
                    })}
                  >
                    {carouselItems.map((item, idx) => {
                      const isCurrent = idx === selectedIndex;

                      return (
                        <Icon
                          key={`${item.id || kebabCase(item?.heading)}-media-carousel-indicator`}
                          color={isCurrent ? 'coolGrey.100' : 'coolGrey.60'}
                          css={isCurrent && { height: '20px', width: '20px', svg: { height: '20px', width: '20px' } }}
                          type={'Ellipse'}
                        />
                      );
                    })}
                  </Box>
                  <CarouselControlButton
                    isMobile
                    aria-controls={id}
                    aria-label={
                      withSmartling('mktg_carousel_slide_next', {
                        slide: `${((selectedIndex + 1) % numItems) + 1}`,
                      }) || 'Go to next slide'
                    }
                    direction="next"
                    onClick={() => handleUpdateCarousel(selectedIndex + 1)}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
        {!(numItems <= 1) && (
          <CarouselControlButton
            aria-controls={id}
            aria-label={
              withSmartling('mktg_carousel_slide_next', { slide: `${((selectedIndex + 1) % numItems) + 1}` }) ||
              'Go to next slide'
            }
            direction="next"
            onClick={() => handleUpdateCarousel(selectedIndex + 1)}
          />
        )}
      </Container>
    </Box>
  );
};

export { MediaCarousel };
export default MediaCarousel;
